'use client';

import { forwardRef, useContext, useEffect, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { DateTimeBlockRootContext } from './context/date-time-block-root.context';
import { DateTimeBlockDay } from './date-time-block-day';
import { useDateTimeBlockDatePagination } from './hooks/use-date-pagination';
import { useDateTimeBlockRenderPagination } from './hooks/use-render-pagination';
import {
  dateTimeBlockDatesInnerRecipe,
  dateTimeBlockDatesOuterRecipe,
} from './styles/date-time-block-dates.styles';
import type { DateTimeBlockDatesPropsInterface } from './types/date-time-block-dates.types';

/**
 * `DateTimeBlock.Dates` provides the interface for selecting dates
 * from the available dates provide.
 *
 * @since 10.2.0
 */
export const DateTimeBlockDates = forwardRef<HTMLDivElement, DateTimeBlockDatesPropsInterface>(
  (props: DateTimeBlockDatesPropsInterface, forwardedRef) => {
    const {
      children,
      className,
      css: cssProp,
      renderDatesNextIconButton,
      renderDatesPrevIconButton,
      ...rest
    } = props;

    const {
      dates,
      datesToShow,
      disabledDates,
      formFieldContext,
      hasTimesComponent,
      includeOffset,
      isTouchScreen,
      selectedDate,
      setHasDatesComponent,
      times,
      touchScreenOffset,
    } = useContext(DateTimeBlockRootContext);

    // Register this component as being rendered
    useEffect(() => {
      if (dates && dates.length > 0) {
        setHasDatesComponent(true);
        return () => setHasDatesComponent(false);
      }
    }, [setHasDatesComponent, dates]);

    const datesRef = useMemo(() => dates || [], [dates]);
    const timesRef = useMemo(() => times || [], [times]);

    /*
     * Use pagination hook to manage date navigation and visibility
     * Always call hooks before any conditional returns
     */
    const { visibleDates, isPrevDisabled, isNextDisabled, handlePrevious, handleNext } =
      useDateTimeBlockDatePagination({
        dates: datesRef,
        datesToShow,
        disabledDates,
        disablePagination: isTouchScreen,
      });

    /**
     * Pass render logic for buttons to hook
     */
    const { prevIconButtonElement, nextIconButtonElement } = useDateTimeBlockRenderPagination({
      renderDatesPrevIconButton,
      renderDatesNextIconButton,
      isPrevDisabled,
      isNextDisabled,
      handlePrevious,
      handleNext,
    });

    // Don't render if no dates provided
    if (!dates || dates.length === 0) {
      return null;
    }

    return (
      <div
        ref={forwardedRef}
        data-c11n-component="DateTimeBlock.Dates"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', css(cssProp), className)}
        id={!hasTimesComponent ? formFieldContext.controlId : undefined}
        role="region"
        aria-label="Date selection carousel"
        {...rest}
      >
        <div
          data-c11n-component="DateTimeBlock.Dates.Outer"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', css(dateTimeBlockDatesOuterRecipe.raw({ includeOffset })))}
        >
          {includeOffset && prevIconButtonElement}
          <div
            data-c11n-component="DateTimeBlock.Dates.Inner"
            data-c11n-version={__C11N_VERSION__}
            className={cx(
              'c11n',
              css(
                dateTimeBlockDatesInnerRecipe.raw({
                  datesToShow,
                  isTouchScreen,
                  touchScreenOffset,
                }),
                cssProp,
              ),
            )}
            aria-labelledby={
              timesRef.length > 0 && !selectedDate ? formFieldContext.descriptionId : undefined
            }
            role="group"
          >
            {visibleDates.map((date) => {
              const dateKey = date instanceof Date ? date.getTime().toString() : date.toString();
              return <DateTimeBlockDay key={dateKey} date={date} />;
            })}
          </div>
          {includeOffset && nextIconButtonElement}
        </div>
        {children}
      </div>
    );
  },
);

DateTimeBlockDates.displayName = 'DateTimeBlock.Dates';
